import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Backend from 'i18next-http-backend'
import queryString from 'query-string'
import { initReactI18next } from 'react-i18next'
import languageMap from './language-map'
import {
  geti18nFromLanguageMap,
  isi18nInsideLanguageMap,
  lcidToXliff,
  MAPi18n2xliff,
} from './utils'

const DEFAULT_LANGUAGE = 'en'

const getValidQueryStringLng = (lng: string): string | undefined => {
  const isLngInLcidFormat = !Number.isNaN(parseInt(lng, 10))

  if (languageMap.has(lng)) {
    return geti18nFromLanguageMap(lng)
  }

  if (isi18nInsideLanguageMap(lng)) {
    return lng
  }

  if (isLngInLcidFormat) {
    const lcidXliff = lcidToXliff(parseInt(lng, 10))
    const lcidXliffLang = lcidXliff || ''
    return languageMap.has(lcidXliffLang)
      ? geti18nFromLanguageMap(lcidXliffLang)
      : undefined
  }

  return undefined
}

// Initialize empty language detector
const lngDetector = new LanguageDetector(null, {
  convertDetectedLanguage: (lng) => MAPi18n2xliff(lng),
})
// Customize language detector
lngDetector.addDetector({
  name: 'mea-detector',
  lookup(options) {
    const queryStringName = options.lookupQuerystring
      ? options.lookupQuerystring
      : 'lng'
    const queryStringLng = queryString
      .parse(window.location.search)
      [queryStringName]?.toString()

    // Look for language inside querystring lng=1029/cs-CZ/cs
    if (queryStringLng) {
      return getValidQueryStringLng(queryStringLng)
    }

    return undefined
  },
})

i18next
  // Load translation using http from /wwwroot/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
  // Learn more: https://github.com/i18next/i18next-http-backend
  .use(Backend)
  // Detect user language
  // Learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(lngDetector)
  // Pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // Init i18next
  // For all options read: https://www.i18next.com/overview/configuration-options
  .init({
    fallbackLng: (code) => {
      const normalized = (code || '').toLowerCase()

      // English - don't need to fallback to English
      if (normalized.startsWith(DEFAULT_LANGUAGE)) {
        return []
      }

      // Other Languages - fallback to the base language (e.g. es from es-CL)
      // and then English
      const [base] = normalized.split('-')
      const baseLong = MAPi18n2xliff(base).toLowerCase()

      return baseLong === normalized
        ? [MAPi18n2xliff(DEFAULT_LANGUAGE)]
        : [baseLong, MAPi18n2xliff(DEFAULT_LANGUAGE)]
    },
    load: 'currentOnly',
    detection: {
      order: [
        'mea-detector',
        'cookie',
        'localStorage',
        'sessionStorage',
        'navigator',
        'htmlTag',
        'path',
        'subdomain',
      ],
      caches: ['localStorage'],
      lookupCookie: 'default_language',
    },
    keySeparator: false,
    interpolation: {
      // React already protects from xss
      escapeValue: false,
    },
    // debug: myESET.isLocalEnv !== 'false',
    react: {
      useSuspense: true,
    },
    backend: {
      // Map long locale codes (e.g. en-US, es-CL) to the actual locale folders (en, es-cl).
      loadPath: (lngs: string | string[], namespaces: string | string[]) => {
        const lng = Array.isArray(lngs) ? lngs[0] : lngs
        const ns = Array.isArray(namespaces) ? namespaces[0] : namespaces
        const mappedLng = (lng && geti18nFromLanguageMap(lng)) || lng
        return `/locales/${mappedLng}/${ns}.json`
      },
      queryStringParams: { v: new Date().getUTCDate() },
    },
  })

i18next.on('languageChanged', (lng) => {
  // Change the <html /> direction for the current lang
  document.documentElement.dir = i18next.dir()
  // Change the <html /> lang attr for the current lang
  document.documentElement.lang = lng
})

export default i18next
